import { useEffect, useRef } from 'react';
import { useToleranceStore } from '../stores/toleranceStore';
import { useAuthStore } from '../stores/authStore';
import { saveRecovery, clearRecovery } from '../lib/recovery';

// Schreibt den aktiven Berechnungs-Zustand debounced in den
// localStorage. Kürzere Debounce-Zeit als beim Cloud-Auto-Save
// (3 s in useAutoSave) — localStorage ist billig, und je weniger
// Datenverlust beim Crash, desto besser. 1 s ist der Kompromiss
// aus "fängt jedes flüchtige Edit" und "tickert nicht bei jeder
// Mikro-Keystroke".
const DEBOUNCE_MS = 1000;

/**
 * Auto-Persist der aktiven Berechnung nach jedem relevanten
 * Store-Change. Wird einmal in CalculatorApp gemountet.
 *
 * Greift sowohl für Free als auch Pro: Pro hat zwar Cloud-Save, aber
 * der greift nur bei aktivem Projekt + Calculation. Für freie
 * Editiersessions (Templates, .tcp-Importe, vor erstem Speichern) ist
 * localStorage das einzige Sicherheitsnetz gegen Reload/Crash.
 *
 * Bei leerer Kette wird der Snapshot gelöscht — sonst würde nach
 * "Neu" + Reload das alte Recovery-Banner wieder auftauchen, was den
 * "Neu"-Befehl konterkariert.
 */
export function useRecoveryAutoSave() {
  // Wir greifen die Felder einzeln aus dem Store, damit der Effect
  // nur auf inhaltliche Änderungen reagiert. Ein selector-as-object
  // würde bei jeder Store-Mutation eine neue Referenz liefern und den
  // Effect grundlos feuern.
  const elements = useToleranceStore((s) => s.elements);
  const m0 = useToleranceStore((s) => s.m0);
  const mode = useToleranceStore((s) => s.mode);
  const thermal = useToleranceStore((s) => s.thermal);
  const calculationName = useToleranceStore((s) => s.calculationName);
  const mcSamples = useToleranceStore((s) => s.mcSamples);
  const mcConfidence = useToleranceStore((s) => s.mcConfidence);
  const mcSeed = useToleranceStore((s) => s.mcSeed);

  // Besitzer des Snapshots: der eingeloggte User. Der Rechner liegt hinter
  // AuthGuard, also ist die id beim Speichern vorhanden. Sie bindet den
  // Snapshot an den Account, damit ihn kein anderer Login angeboten bekommt.
  const ownerId = useAuthStore((s) => s.user?.id ?? null);

  const timerRef = useRef(null);
  // Vorherige Element-Länge mitführen, damit clearRecovery() nur beim
  // tatsächlichen Übergang "war was drin → jetzt leer" feuert. Sonst
  // würde der Hook beim ersten Mount nach einem Page-Reload (elements
  // initial []) den gerade noch existierenden Snapshot löschen, bevor
  // RecoveryBanner ihn lesen kann — die Race-Bedingung war der Grund
  // dafür, dass das Banner nach F5 nie auftauchte.
  const prevLenRef = useRef(elements?.length ?? 0);
  // Aktuellen Snapshot in einer Ref mitführen, damit der einmal
  // registrierte beforeunload-Handler immer auf die NEUESTEN Werte
  // zugreifen kann (ohne den Listener bei jedem State-Change neu
  // attachen zu müssen — Browser-Limits, plus saubere DX). Update
  // läuft in einem eigenen Effect statt während Render, weil
  // ref-Writes im Render-Pfad vom React-Compiler/Linter gefiltert
  // werden (react-hooks/refs).
  const latestRef = useRef(null);
  useEffect(() => {
    latestRef.current = {
      elements, m0, mode, thermal,
      calculationName, mcSamples, mcConfidence, mcSeed,
    };
  }, [elements, m0, mode, thermal, calculationName, mcSamples, mcConfidence, mcSeed]);

  // ownerId separat in einer Ref, damit der einmalig registrierte
  // beforeunload-Handler immer die aktuelle id sieht.
  const ownerIdRef = useRef(ownerId);
  useEffect(() => { ownerIdRef.current = ownerId; }, [ownerId]);

  useEffect(() => {
    const currLen = elements?.length ?? 0;
    const prevLen = prevLenRef.current;
    prevLenRef.current = currLen;

    if (currLen === 0) {
      // Nur clearen, wenn der User aktiv geleert hat (Neu-Klick,
      // Logout etc.). Initial-Mount mit leeren elements darf den
      // Recovery-Snapshot vom letzten Browsing nicht zerstören.
      if (prevLen > 0) clearRecovery();
      return;
    }

    // Ohne eingeloggten User kein Snapshot (defensiv — hinter AuthGuard
    // ist die id da). saveRecovery selbst verwirft einen Save ohne ownerId.
    if (!ownerId) return;
    // Versions-Vorschau: der alte Stand ist keine „letzte Arbeit“.
    if (useToleranceStore.getState().preview) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      saveRecovery({
        elements,
        m0,
        mode,
        thermal,
        calculationName,
        mcSamples,
        mcConfidence,
        mcSeed,
      }, ownerId);
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [elements, m0, mode, thermal, calculationName, mcSamples, mcConfidence, mcSeed, ownerId]);

  // Sofortspeicherung beim Verlassen der Seite (Reload, Tab schließen,
  // URL ändern). Schließt das 1-Sekunden-Debounce-Fenster: ohne diesen
  // Listener würden Edits, die direkt vor F5 gemacht wurden, verloren
  // gehen, weil der setTimeout noch nicht gefeuert hatte. Wir
  // verlassen uns auf das synchrone localStorage.setItem — async
  // Operationen (sendBeacon, fetch) werden vom Browser im
  // beforeunload-Pfad nicht garantiert zu Ende gebracht.
  //
  // Kein preventDefault / returnValue — wir wollen ausdrücklich KEINEN
  // Browser-Dialog. Recovery soll geräuschlos wirken; ein Dialog war
  // genau der Grund, warum der frühere useUnsavedChangesGuard
  // gestrichen wurde.
  useEffect(() => {
    const onBeforeUnload = () => {
      if (useToleranceStore.getState().preview) return;
      const snap = latestRef.current;
      const oid = ownerIdRef.current;
      if (oid && snap && snap.elements && snap.elements.length > 0) {
        saveRecovery(snap, oid);
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);
}
